import { useState, useCallback, useRef } from 'react';
import api from '../lib/api';
import type {
  CFEntity, CFMonthlyData, CFTopEntry, LoanSelfFunding,
} from '../types/campaignFinance';
import { CF_REQUEST_TIMEOUT_MS } from '../utils/campaignFinanceUtils';

const PAGE_SIZE = 100;

/**
 * Scope for the transactions explorer. Committee views set committee_co_id; the
 * general search passes the full filter set (contribution-only filters are ignored
 * by the expenditures/loans endpoints, which is fine).
 */
export interface CFEntityScope {
  committee_co_id?: string;
  committee_name?: string;
  contributor?: string;
  candidate?: string;
  contribution_type?: string;
  contributor_type?: string;
  committee_type?: string;
  min_amount?: string;
  max_amount?: string;
  employer?: string;
  year?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  // Expenditures-mode scope (only the expenditures endpoint reads these).
  payee?: string;
  vendor_id?: string;
  expenditure_type?: string;
  payment_type?: string;
  disbursement_type?: string;
}

interface EntityConfig {
  url: string;
  rowsKey: 'contributions' | 'expenditures' | 'loans';
  topKey: 'topContributors' | 'topPayees' | 'topLenders';
  defaultSort: string;
}

const CONFIG: Record<CFEntity, EntityConfig> = {
  contributions: { url: '/campaign-finance', rowsKey: 'contributions', topKey: 'topContributors', defaultSort: 'contribution_date' },
  expenditures: { url: '/campaign-finance/expenditures', rowsKey: 'expenditures', topKey: 'topPayees', defaultSort: 'expenditure_date' },
  loans: { url: '/campaign-finance/loans', rowsKey: 'loans', topKey: 'topLenders', defaultSort: 'loan_date' },
};

export type SortDir = 'asc' | 'desc';

export interface CFEntityState {
  rows: any[];
  total: number;
  grouped: boolean;
  groupBy: string;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  summary: any | null;
  monthlyTrend: CFMonthlyData[];
  topList: CFTopEntry[];
  selfFunding: LoanSelfFunding | null;
  loaded: boolean;
  sortBy: string;
  sortDir: SortDir;
}

export interface UseCFEntityReturn extends CFEntityState {
  load: (scope: CFEntityScope, groupBy?: string) => Promise<void>;
  setGroupBy: (groupBy: string) => void;
  setSort: (field: string, dir: SortDir) => void;
  loadMore: () => Promise<void>;
  showAll: () => Promise<void>;
  /** Fetch the individual (ungrouped) records within a group, reusing the current scope. */
  fetchRecords: (extra: Record<string, string>) => Promise<any[]>;
  reset: () => void;
}

function buildParams(
  cfg: EntityConfig,
  scope: CFEntityScope,
  groupBy: string,
  extra: { offset: number; limit: number; sortBy?: string; sortDir?: SortDir },
): URLSearchParams {
  const p = new URLSearchParams();
  p.set('offset', String(extra.offset));
  p.set('limit', String(extra.limit));
  p.set('sort_by', extra.sortBy || cfg.defaultSort);
  p.set('sort_order', extra.sortDir || 'desc');
  if (scope.committee_co_id) {
    // Scope every entity by co_id: it is present on all three tables (a committee's
    // transactions all carry its CO_ID), indexed, and — unlike committee_co_id —
    // never depends on the spine backfill having run. Filtering on committee_co_id
    // would silently return nothing if that FK column isn't populated yet.
    p.set('co_id', scope.committee_co_id);
  }
  if (scope.committee_name) p.set('committee_name', scope.committee_name);
  if (scope.contributor) p.set('contributor_name', scope.contributor);
  if (scope.candidate) p.set('candidate_name', scope.candidate);
  if (scope.contribution_type) p.set('contribution_type', scope.contribution_type);
  if (scope.contributor_type) p.set('contributor_type', scope.contributor_type);
  if (scope.committee_type) p.set('committee_type', scope.committee_type);
  if (scope.min_amount) p.set('min_amount', scope.min_amount);
  if (scope.max_amount) p.set('max_amount', scope.max_amount);
  if (scope.employer) p.set('employer', scope.employer);
  if (scope.year) p.set('year', scope.year);
  if (scope.start_date) p.set('start_date', scope.start_date);
  if (scope.end_date) p.set('end_date', scope.end_date);
  if (scope.search) p.set('search', scope.search);
  if (scope.payee) p.set('payee', scope.payee);
  if (scope.vendor_id) p.set('vendor_id', scope.vendor_id);
  if (scope.expenditure_type) p.set('expenditure_type', scope.expenditure_type);
  if (scope.payment_type) p.set('payment_type', scope.payment_type);
  if (scope.disbursement_type) p.set('disbursement_type', scope.disbursement_type);
  if (groupBy && groupBy !== 'none') p.set('group_by', groupBy);
  return p;
}

/**
 * Generic list hook for any campaign-finance transaction entity, scoped to one
 * committee. Each fetch always returns summary / monthlyTrend / topList (and, for
 * loans, the self-funding rollup) for the same filter set — so toggling group_by
 * re-derives the rows without losing the aggregates the dashboard charts read.
 */
export function useCFEntity(entity: CFEntity): UseCFEntityReturn {
  const cfg = CONFIG[entity];
  const [state, setState] = useState<CFEntityState>({
    rows: [], total: 0, grouped: false, groupBy: 'none', hasMore: false,
    loading: false, error: null, summary: null, monthlyTrend: [], topList: [],
    selfFunding: null, loaded: false, sortBy: cfg.defaultSort, sortDir: 'desc',
  });

  const scopeRef = useRef<CFEntityScope>({});
  const groupByRef = useRef<string>('none');
  const sortByRef = useRef<string>(cfg.defaultSort);
  const sortDirRef = useRef<SortDir>('desc');
  const offsetRef = useRef(0);

  const fetchPage = useCallback(async (scope: CFEntityScope, groupBy: string, offset: number, limit: number, sortBy: string, sortDir: SortDir) => {
    const params = buildParams(cfg, scope, groupBy, { offset, limit, sortBy, sortDir });
    const res = await api.get(`${cfg.url}?${params.toString()}`, { timeout: CF_REQUEST_TIMEOUT_MS });
    if (!res.data?.success) throw new Error(res.data?.error || 'Request failed');
    return res.data.data;
  }, [cfg]);

  const load = useCallback(async (scope: CFEntityScope, groupBy: string = 'none') => {
    scopeRef.current = scope;
    groupByRef.current = groupBy;
    // A new search/scope resets sort to the entity default.
    sortByRef.current = cfg.defaultSort;
    sortDirRef.current = 'desc';
    offsetRef.current = 0;
    // Clear the previously-displayed data immediately so a new search shows a clean
    // loading state instead of stale results while the (slow) query runs.
    setState(s => ({
      ...s, loading: true, error: null,
      rows: [], total: 0, grouped: false, summary: null, monthlyTrend: [], topList: [], selfFunding: null,
    }));
    try {
      const data = await fetchPage(scope, groupBy, 0, PAGE_SIZE, cfg.defaultSort, 'desc');
      offsetRef.current = PAGE_SIZE;
      setState({
        rows: data[cfg.rowsKey] || [],
        total: data.total || 0,
        grouped: !!data.grouped,
        groupBy,
        hasMore: !!data.hasMore,
        loading: false,
        error: null,
        summary: data.summary || null,
        monthlyTrend: data.monthlyTrend || [],
        topList: data[cfg.topKey] || [],
        selfFunding: data.selfFunding || null,
        loaded: true,
        sortBy: cfg.defaultSort,
        sortDir: 'desc',
      });
    } catch (err: any) {
      setState(s => ({
        ...s,
        loading: false,
        loaded: true,
        rows: [],
        total: 0,
        error: err?.response?.data?.error || err?.message || `Failed to load ${entity}`,
      }));
    }
  }, [cfg, entity, fetchPage]);

  const setGroupBy = useCallback((groupBy: string) => {
    void load(scopeRef.current, groupBy);
  }, [load]);

  // Re-fetch the current scope/grouping with a new sort (flat ledger column sort).
  const setSort = useCallback(async (field: string, dir: SortDir) => {
    sortByRef.current = field;
    sortDirRef.current = dir;
    offsetRef.current = 0;
    setState(s => ({ ...s, loading: true, sortBy: field, sortDir: dir }));
    try {
      const data = await fetchPage(scopeRef.current, groupByRef.current, 0, PAGE_SIZE, field, dir);
      offsetRef.current = PAGE_SIZE;
      setState(s => ({
        ...s,
        rows: data[cfg.rowsKey] || [],
        total: data.total || 0,
        grouped: !!data.grouped,
        hasMore: !!data.hasMore,
        loading: false,
      }));
    } catch (err: any) {
      setState(s => ({ ...s, loading: false, error: err?.message || `Failed to sort ${entity}` }));
    }
  }, [cfg, entity, fetchPage]);

  const loadMore = useCallback(async () => {
    if (state.loading || !state.hasMore) return;
    try {
      const data = await fetchPage(scopeRef.current, groupByRef.current, offsetRef.current, PAGE_SIZE, sortByRef.current, sortDirRef.current);
      offsetRef.current += PAGE_SIZE;
      setState(s => ({
        ...s,
        rows: [...s.rows, ...(data[cfg.rowsKey] || [])],
        hasMore: !!data.hasMore,
      }));
    } catch {
      /* keep current rows on a failed page append */
    }
  }, [cfg, fetchPage, state.loading, state.hasMore]);

  const showAll = useCallback(async () => {
    if (state.total <= 0) return;
    setState(s => ({ ...s, loading: true }));
    try {
      const data = await fetchPage(scopeRef.current, groupByRef.current, 0, state.total, sortByRef.current, sortDirRef.current);
      offsetRef.current = state.total;
      setState(s => ({
        ...s,
        rows: data[cfg.rowsKey] || [],
        hasMore: false,
        loading: false,
      }));
    } catch (err: any) {
      setState(s => ({ ...s, loading: false, error: err?.message || 'Failed to load all rows' }));
    }
  }, [cfg, fetchPage, state.total]);

  // Fetch the ungrouped records inside a group (for inline expansion), reusing the
  // current scope so the children match the group's aggregate exactly.
  const fetchRecords = useCallback(async (extra: Record<string, string>) => {
    const params = buildParams(cfg, scopeRef.current, 'none', { offset: 0, limit: 250, sortBy: cfg.defaultSort, sortDir: 'desc' });
    Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    const res = await api.get(`${cfg.url}?${params.toString()}`, { timeout: CF_REQUEST_TIMEOUT_MS });
    if (!res.data?.success) return [];
    return res.data.data?.[cfg.rowsKey] || [];
  }, [cfg]);

  const reset = useCallback(() => {
    scopeRef.current = {};
    groupByRef.current = 'none';
    sortByRef.current = cfg.defaultSort;
    sortDirRef.current = 'desc';
    offsetRef.current = 0;
    setState({
      rows: [], total: 0, grouped: false, groupBy: 'none', hasMore: false,
      loading: false, error: null, summary: null, monthlyTrend: [], topList: [],
      selfFunding: null, loaded: false, sortBy: cfg.defaultSort, sortDir: 'desc',
    });
  }, [cfg]);

  return { ...state, load, setGroupBy, setSort, loadMore, showAll, fetchRecords, reset };
}
